/**
 * DeleteAccountModal — Self-Service-Konto-Löschung (TQ #10084).
 *
 * Zwei-Stufen-Bestätigung ("Bist du sicher?" → "Wirklich sicher?") + optionale
 * Passwort-Reverifikation (Personal-/Solo-User, `requiresPassword=true`).
 * Org-Owner (`requiresPassword=false`) werden vom Backend ohnehin VOR der
 * Passwort-Prüfung geblockt (Owner-Block-Path) — das Passwortfeld entfällt
 * für sie, die zweite Stufe zeigt nur noch den Blockierungs-Grund an, wenn
 * die Anfrage mit 409 zurückkommt.
 *
 * KEIN E-Mail-Bestätigungslink (Coordinator-Entscheidung 24.07., TQ #10084).
 */
import { useState } from 'react'
import { ConfirmDialog } from '@it-sce/ui'
import { api } from '../services/api'
import { useToast } from './ui'

type Step = 'closed' | 'confirm1' | 'confirm2'

interface Props {
  /** true für Solo-/Personal-Mode-User (Backend verlangt Passwort-Reverifikation). */
  requiresPassword: boolean
  /** Aufgerufen nach erfolgreicher Löschung — Aufrufer entscheidet über Logout/Redirect. */
  onDeleted: () => void
}

export default function DeleteAccountModal({ requiresPassword, onDeleted }: Props) {
  const { showToast } = useToast()
  const [step, setStep] = useState<Step>('closed')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  function open() {
    setPassword('')
    setError(null)
    setStep('confirm1')
  }

  function cancel() {
    if (submitting) return
    setStep('closed')
    setPassword('')
    setError(null)
  }

  async function handleFinalConfirm() {
    if (requiresPassword && !password) {
      setError('Bitte gib dein aktuelles Passwort ein.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const res = await api.deleteMyAccount(requiresPassword ? password : '')
      showToast('success', res.message || 'Konto zur Löschung vorgemerkt.')
      setStep('closed')
      onDeleted()
    } catch (err: any) {
      const data = err?.response?.data
      const status = err?.response?.status
      if (data?.blocked) {
        setError(data.error || 'Du bist Eigentümer einer Organisation und kannst dein Konto nicht direkt löschen.')
      } else if (status === 401) {
        setError('Passwort falsch.')
      } else {
        console.error('[DeleteAccountModal] deleteMyAccount failed:', err)
        setError(data?.error || 'Konto-Löschung fehlgeschlagen. Bitte später erneut versuchen oder Support kontaktieren.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={open}
        style={{
          border: '1px solid #fecaca', background: '#fff', color: '#dc2626',
          padding: '0.6rem 1rem', borderRadius: 8, cursor: 'pointer', fontSize: '0.9rem',
        }}
      >
        Konto löschen
      </button>

      <ConfirmDialog
        isOpen={step === 'confirm1'}
        title="Konto wirklich löschen?"
        variant="danger"
        confirmLabel="Weiter"
        message={
          <>
            Dein Konto wird gesperrt und nach einer 30-tägigen Karenzzeit endgültig
            gelöscht (Stammdaten anonymisiert, Login entfernt). Diese Aktion kann
            danach nicht mehr rückgängig gemacht werden.
          </>
        }
        onConfirm={() => setStep('confirm2')}
        onCancel={cancel}
      />

      <ConfirmDialog
        isOpen={step === 'confirm2'}
        title="Wirklich sicher? Diese Aktion ist unwiderruflich."
        variant="danger"
        confirmLabel={submitting ? '…' : 'Konto endgültig löschen'}
        loading={submitting}
        message={
          <div>
            <p style={{ margin: '0 0 0.75rem' }}>
              Nach Ablauf der Karenzzeit werden deine Daten anonymisiert und dein
              Login entfernt — bitte bestätige das erneut.
            </p>
            {requiresPassword && (
              <div style={{ margin: '0 0 0.75rem' }}>
                <label
                  htmlFor="delete-account-password"
                  style={{ display: 'block', fontSize: '0.85rem', color: '#374151', marginBottom: '0.25rem' }}
                >
                  Aktuelles Passwort zur Bestätigung
                </label>
                <input
                  id="delete-account-password"
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  autoFocus
                  disabled={submitting}
                  style={{
                    width: '100%', padding: '0.6rem 0.75rem', borderRadius: 8,
                    border: '1px solid #d1d5db', fontFamily: 'inherit', fontSize: '0.9rem',
                  }}
                />
              </div>
            )}
            {error && (
              <p style={{ margin: 0, color: '#dc2626', fontSize: '0.85rem' }}>{error}</p>
            )}
          </div>
        }
        onConfirm={handleFinalConfirm}
        onCancel={cancel}
      />
    </>
  )
}
